import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Trash, TrashSimple, ArrowCounterClockwise, SpinnerGap } from "@phosphor-icons/react";

export function TrashTab() {
  const { api } = useAuth();
  const [trashItems, setTrashItems] = useState([]);
  const [trashLoading, setTrashLoading] = useState(true);
  const [trashRetention, setTrashRetention] = useState(30);

  const loadTrash = useCallback(async () => {
    setTrashLoading(true);
    try {
      const resp = await api.get("/trash");
      setTrashItems(resp.data.items || []);
      setTrashRetention(resp.data.retention_days || 30);
    } catch { /* silent */ }
    setTrashLoading(false);
  }, [api]);

  useEffect(() => { loadTrash(); }, [loadTrash]);

  const restoreTrashItem = async (trashType, itemId) => {
    try {
      await api.post(`/trash/restore/${trashType}/${itemId}`);
      toast.success("Item restored");
      loadTrash();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to restore"); }
  };

  const permanentlyDelete = async (trashType, itemId) => {
    try {
      await api.delete(`/trash/${trashType}/${itemId}`);
      toast.success("Permanently deleted");
      loadTrash();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  const emptyTrash = async () => {
    if (!window.confirm("Permanently delete all items in trash? This cannot be undone.")) return;
    try {
      const resp = await api.delete("/trash");
      toast.success(resp.data.message);
      setTrashItems([]);
    } catch { toast.error("Failed to empty trash"); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4" data-testid="trash-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-['Cormorant_Garamond'] text-lg font-semibold text-[var(--text-primary)]">Trash</h2>
          <p className="text-xs text-[var(--text-muted)]">
            Deleted items are kept for {trashRetention} days, then permanently removed.
            <a href="/legal/data-retention" target="_blank" className="text-[#FF4400] hover:underline ml-1">Policy</a>
          </p>
        </div>
        {trashItems.length > 0 && (
          <Button onClick={emptyTrash} variant="outline" size="sm" className="border-red-500/20 text-red-400 hover:bg-red-500/10 text-xs" data-testid="empty-trash-btn">
            <Trash size={14} className="mr-1" /> Empty Trash
          </Button>
        )}
      </div>

      {trashLoading ? (
        <div className="flex justify-center py-12"><SpinnerGap size={28} className="animate-spin text-[#FF4400]" /></div>
      ) : trashItems.length === 0 ? (
        <div className="text-center py-16 bg-[var(--bg-card)] border border-[var(--border)] rounded-xl" data-testid="trash-empty">
          <TrashSimple size={36} className="mx-auto text-[var(--text-muted)] mb-3" />
          <p className="text-sm text-[var(--text-muted)]">Trash is empty</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="trash-items-list">
          {trashItems.map((item) => {
            const daysLeft = item.trashed_at
              ? Math.max(0, trashRetention - Math.floor((Date.now() - new Date(item.trashed_at).getTime()) / 86400000))
              : trashRetention;
            return (
              <div
                key={`${item.trash_type}-${item.item_id}`}
                className="flex items-center gap-3 p-3 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg hover:border-[var(--border-strong)] transition-colors"
                data-testid={`trash-item-${item.item_id}`}
              >
                <div className={`w-8 h-8 rounded-md flex items-center justify-center text-xs font-semibold shrink-0 ${
                  item.trash_type === "content" ? "bg-blue-500/10 text-blue-400" :
                  item.trash_type === "resource" ? "bg-emerald-500/10 text-emerald-400" :
                  "bg-amber-500/10 text-amber-400"
                }`}>
                  {item.trash_type === "content" ? "C" : item.trash_type === "resource" ? "R" : "F"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--text-primary)] truncate">{item.title || item.topic || "Untitled"}</p>
                  <p className="text-[10px] text-[var(--text-muted)]">
                    {item.trash_type === "content" ? item.content_type : item.trash_type === "resource" ? item.source_type : "File"}
                    {" \u00B7 "}{daysLeft} day{daysLeft !== 1 ? "s" : ""} left
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={() => restoreTrashItem(item.trash_type, item.item_id)}
                    className="p-1.5 rounded-md text-[var(--text-muted)] hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors"
                    title="Restore"
                    data-testid={`restore-${item.item_id}`}
                  >
                    <ArrowCounterClockwise size={16} />
                  </button>
                  <button
                    onClick={() => permanentlyDelete(item.trash_type, item.item_id)}
                    className="p-1.5 rounded-md text-[var(--text-muted)] hover:text-red-400 hover:bg-red-500/10 transition-colors"
                    title="Delete permanently"
                    data-testid={`perm-delete-${item.item_id}`}
                  >
                    <Trash size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}
